import type { KeyboardInputItem } from '@vedalib/editor/lib/elements'
import { generateId } from '@vedalib/editor/lib/utils/id'
import * as R from 'ramda'
import { useCallback } from 'react'
import React from 'react'

import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import { chainName, FormField, FieldEditable } from 'components/formKit'
import type { IRenderItemProps } from 'components/uiKit/ArrayControl'
import { IconButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { t } from 'services/Translation'

import * as s from './TestKeyboardInputFormValueItem.module.scss'

const LABEL = genElementValueField({
  name: 'label',
  as: FieldEditable,
  size: KitSize.M,
  props: { placeholder: t('input.placeholder.writeAnAnswer'), fluid: true },
})

const TestKeyboardInputFormValueItem: React.FC<IRenderItemProps<KeyboardInputItem>> = ({
  index,
  name,
  onChange,
  value: array,
  min,
  max,
}) => {
  const disableClone = Boolean(max && array.length >= max)
  const disableDelete = Boolean(min && array.length <= min)

  const onClone = useCallback(() => {
    return onChange?.([...array, { ...R.clone(array[index]), value: generateId() }], {
      command: 'clone',
      item: array[index],
    })
  }, [onChange, array, index])

  const onDelete = useCallback(() => {
    return onChange?.(
      array.filter((_item, i) => i !== index),
      { command: 'delete', item: array[index] },
    )
  }, [onChange, array, index])

  return (
    <div className={s.root}>
      <div className={s.header}>
        <Typography>
          {t('elements.testKeyboardInput.value.answer')} {index + 1}
        </Typography>
        <div className={s.actions}>
          <Tooltip overlay={t('uiKit.tooltip.duplicate')}>
            <IconButton
              disabled={disableClone}
              icon='otherCopy'
              name='clone'
              onClick={onClone}
              size={KitSize.XS}
              styleType='ghost'
            />
          </Tooltip>
          <Tooltip overlay={t('uiKit.tooltip.delete')}>
            <IconButton
              disabled={disableDelete}
              icon='otherTrash'
              name='delete'
              onClick={onDelete}
              size={KitSize.XS}
              styleType='ghost'
            />
          </Tooltip>
        </div>
      </div>
      <Padding bottom gap sides top>
        <FormField config={chainName(name, `${index}`, LABEL)} />
      </Padding>
    </div>
  )
}

export default React.memo(TestKeyboardInputFormValueItem)
